import type { TFunction } from "i18next";
import type { AxiosError } from "axios";

/**
 * Estructura de error del backend
 */
interface BackendError {
  meta?: {
    status?: number;
    message?: {
      [key: number]: Array<{ path?: string; msg: string }>;
    } | string;
    error?: string;
  };
  message?: string;
  error?: string;
}

/**
 * Extrae el mensaje de error de diferentes estructuras de respuesta
 */
export const getError = (
  error: BackendError | AxiosError | any,
  t?: TFunction<"translation", undefined>
): string => {
  // Si es un error de Axios
  if (error?.isAxiosError || error?.response) {
    const axiosError = error as AxiosError<BackendError>;
    const responseData = axiosError.response?.data;
    
    if (responseData) {
      return extractErrorMessage(responseData, t);
    }
    
    // Errores de red
    if (axiosError.code === "ECONNABORTED") {
      return t ? t("errors.timeout") : "La solicitud tardó demasiado";
    }
    if (axiosError.code === "ERR_NETWORK") {
      return t ? t("errors.network") : "Error de conexión";
    }
    
    return axiosError.message || (t ? t("errors.unknown") : "Error desconocido");
  }

  // Si es un objeto de error del backend
  return extractErrorMessage(error, t);
};

/**
 * Extrae el mensaje de error de la estructura del backend
 */
const extractErrorMessage = (
  data: BackendError | any,
  t?: TFunction<"translation", undefined>
): string => {
  // Estructura 1: meta.message con array de errores por status
  if (data?.meta?.message && typeof data.meta.message === "object") {
    const statusCode = data.meta.status;
    const messagesForStatus = data.meta.message[statusCode];

    if (Array.isArray(messagesForStatus) && messagesForStatus.length > 0) {
      const errorMessages = messagesForStatus.map((err: any) => {
        const msg = err.msg || "Error desconocido";

        // Para errores de validación, mostrar solo el mensaje sin el nombre del campo
        // El mensaje ya contiene la información necesaria
        return msg;
      });
      return errorMessages.join(", ");
    }
  }

  // Estructura 2: meta.message como string directo
  if (data?.meta?.message && typeof data.meta.message === "string") {
    return data.meta.message;
  }

  // Estructura 3: meta.error
  if (data?.meta?.error) {
    return data.meta.error;
  }

  // Estructura 4: message en raíz
  if (data?.message) {
    return data.message;
  }

  // Estructura 5: error en raíz
  if (data?.error) {
    return data.error;
  }

  // Fallback
  return t ? t("errors.unknown") : "Error desconocido";
};

/**
 * Verifica si el error es un error de autenticación
 */
export const isAuthError = (error: any): boolean => {
  if (error?.isAxiosError || error?.response) {
    const status = error.response?.status;
    return status === 401 || status === 403;
  }
  return false;
};

/**
 * Verifica si el error es un error de red
 */
export const isNetworkError = (error: any): boolean => {
  if (error?.isAxiosError) {
    return error.code === "ERR_NETWORK" || error.code === "ECONNABORTED";
  }
  return false;
};
